/**
 * 월 달력의 일정 막대 배치 — **여러 날 걸치는 일정을 한 줄로 이어 그리기 위한 자리 계산** (v2.182.0).
 *
 * 사용자 요청(2026-09-16):
 *   「웍스캘린더처럼 연속되는 일정은 각각 날짜에 적지 말고 이어지도록 표시.
 *    같은 날에 일정이 4개 이상이면 「외 N건」이 되니 표시 우선순위를 정하자 —
 *    ①연차·휴가 ②[납품] ③시간 순 ④등록 순」
 *
 * ⭐⭐**왜 「줄(lane)」이 필요한가.** 종전에는 날마다 그 날의 목록을 따로 그려서, 3일짜리 일정이
 *   날마다 **다른 높이**에 놓였다. 그러면 옆칸과 이어 붙여도 계단처럼 어긋난다.
 *   그래서 **한 주를 통째로 놓고** 일정마다 줄 번호를 정한다 — 같은 일정은 그 주 내내 같은 줄이다.
 *
 * ⭐**한 주씩 따로 계산한다.** 주가 바뀌면 줄 번호도 새로 정한다(웍스·구글 캘린더와 같다).
 *   달력은 주 단위로 줄바꿈되므로, 주를 넘겨 같은 줄을 지킬 이유가 없다.
 *
 * ⭐**차례가 곧 줄 번호다.** 먼저 온 일정이 위 줄을 가져간다 — 그래서 부르는 쪽이
 *   **우선순위대로 정렬해서 넘겨야 한다.** 자리(`maxLanes`)를 못 얻은 일정은 「외 N건」이 된다.
 *
 * ⚠이 파일에는 React·달력 화면 지식이 없다 — 날짜 글자와 칸 번호만 다룬다.
 *   그래서 그물(`npm --prefix frontend run check:calendar`)이 화면 없이 그대로 부를 수 있다.
 */

import type { WorksEvent } from './calendar-api';

/** 한 칸에 글자로 보여 줄 일정 줄 수 — 넘치면 「외 N건」 (v2.139.1 에 3 → 4 로 늘린 값) */
export const MAX_LANES = 4;

/** 납품 일정인가 — **서버가 번호로 판정한 출처**. 색과 자물쇠가 함께 걸리는 자리다. */
export function isDelivery(e: WorksEvent): boolean {
  return e.source === 'sales';
}

/**
 * 제목 맨 앞의 대괄호 — 그 안의 글자만 꺼낸다. `［］`(전각)도 받는다.
 *
 * ⭐**8자까지만** 본다. 휴가 표시는 늘 짧다(`연차`2 · `오전반차`4 · `경조휴가`4 · `오전 반차`5).
 *   상한을 느슨하게 두면 **「[9월 연차 계획 회의] 준비」가 휴가로 빨개진다** — 처음 12자로 두었다가
 *   그물(`check:calendar` ①)에 잡힌 자리다. 상한을 줄일 때 이 예시를 함께 볼 것.
 */
const HEAD_TAG = /^\s*[[［]([^\]］]{0,8})[\]］]/;

/**
 * 휴가 일정인가 — **색과 차례를 고르는 데만** 쓴다 (v2.182.0 · 사용자 요청 2026-09-16
 * 「일정 중에 [연차] 는 빨간 배경으로 표시하면 좋겠음」 + 반차 등 휴가류도 함께).
 *
 * ⭐**대괄호 「안」에 연차·반차·휴가가 있는지**로 본다. 「[연차] 로 시작」만 보면
 *   `[오전반차]`·`[경조휴가]` 를 놓치고, 대괄호를 안 보면 「연차 계획 회의」가 빨개진다.
 *   `[연차]` · `[연차] 홍길동` · `[반차]` · `[오전 반차]` · `[휴가]` 가 모두 잡힌다.
 * ⭐**「신청」이 붙으면 휴가가 아니다**(v2.214.0 · 사용자 지시 「[휴가신청] 은 빨간색 말고 일반 일정들의 색깔로」) —
 *   휴램에 신청만 된 휴가(`[휴가신청] 황재상`)는 아직 확정이 아니다. 최종 승인되면 `[연차] 황재상` 으로 바뀌며 빨개진다.
 *   색뿐 아니라 칸 안의 차례도 일반 일정과 같다.
 *
 * ⚠⚠**일정 화면의 규칙은 「제목 글자로 판정하지 말 것」이다 — 그것은 「잠글지 말지」의 이야기다.**
 *   웍스에서 누구나 `[납품]` 을 따라 칠 수 있으니 **권한·잠금은 서버가 번호로만** 판정한다.
 *   색·차례는 원래 보기 편하라고 붙이는 것이라 글자로 골라도 된다.
 *   ⛔이 함수를 잠금·수정 가능 여부에 끌어 쓰지 말 것.
 */
export function isLeave(e: WorksEvent): boolean {
  const inside = HEAD_TAG.exec(e.summary ?? '')?.[1];
  return inside != null && /연차|반차|휴가/.test(inside) && !inside.includes('신청');
}

/**
 * 납품 일정처럼 보이는가 — **차례를 정하는 데만** 쓴다
 * (사용자 지시 「그다음은 [납품] 으로 시작되는거」).
 * ⭐두 가지를 다 본다: 영업진행이 만든 것(`source`, 가장 확실)**과** 제목이 `[납품…]` 으로
 *   시작하는 것. 사람이 웍스에서 손으로 적은 납품 일정도 사용자에게는 똑같은 납품 일정이다.
 *   (`[납품]`·`[납품예정]` 둘 다 운영에 있다.)
 */
export function isDeliveryLike(e: WorksEvent): boolean {
  if (isDelivery(e)) return true;
  const inside = HEAD_TAG.exec(e.summary ?? '')?.[1];
  return inside != null && inside.startsWith('납품');
}

/** 달력 칸에서 먼저 보여 줄 무리 — 작을수록 위 (0 휴가 · 1 납품 · 2 나머지) */
export function groupOf(e: WorksEvent): number {
  if (isLeave(e)) return 0;
  if (isDeliveryLike(e)) return 1;
  return 2;
}

/**
 * 달력 칸의 차례 (사용자 지시 2026-09-16) —
 *   ①연차·휴가 ②[납품] ③일정 시간 순서 ④등록한 순서
 *
 * ⭐**종일 일정이 시간 일정보다 위**다. 웍스·구글 캘린더가 그렇고, 사용자 예시
 *   (「종일인 일정 2개면 먼저 등록한 것부터」)도 종일끼리의 이야기였다.
 * ⚠⚠**「등록한 시각」은 자료에 없다.** 웍스 응답에도, 우리 표(`portal_calendar_event`) 에도
 *   전 일정의 등록 시각은 없다(우리 표는 포털에서 만든 것만 안다). 그래서 **받은 차례**(`i`)를
 *   그 자리에 쓴다 — 서버가 (날짜 → 종일 → 시각) 으로 정렬해 주므로 그 안의 동점은
 *   웍스가 준 차례 그대로다. **늘 같은 차례로 보인다는 것**까지는 보장되지만,
 *   그것이 등록 차례라고 단정하지는 않는다.
 */
export function compareForCell(
  a: { e: WorksEvent; i: number },
  b: { e: WorksEvent; i: number },
): number {
  const ga = groupOf(a.e);
  const gb = groupOf(b.e);
  if (ga !== gb) return ga - gb;
  if (a.e.allDay !== b.e.allDay) return a.e.allDay ? -1 : 1;
  const ta = a.e.startTime ?? '';
  const tb = b.e.startTime ?? '';
  if (ta !== tb) return ta.localeCompare(tb);
  return a.i - b.i;
}

export interface CalendarBar<T> {
  item: T;
  /** 0-based 줄 번호 (0 = 맨 위) */
  lane: number;
  /** 이 주에서 막대가 시작하는 칸 (0=일요일 … 6=토요일) */
  from: number;
  /** 이 주에서 막대가 끝나는 칸 */
  to: number;
  /** 일정이 **진짜로** 이 주에서 시작하나 (아니면 지난 주에서 넘어온 것) */
  startsHere: boolean;
  /** 일정이 **진짜로** 이 주에서 끝나나 (아니면 다음 주로 이어진다) */
  endsHere: boolean;
}

export interface WeekLayout<T> {
  bars: CalendarBar<T>[];
  /** 칸마다 자리를 못 얻어 숨은 건수 — 「외 N건」에 쓴다 (길이 7) */
  overflow: number[];
  /** 실제로 쓴 줄 수 */
  laneCount: number;
}

/**
 * 한 주의 막대 자리를 정한다.
 *
 * @param weekDays 그 주 7칸의 날짜 글자. 달 바깥 칸은 `null`.
 * @param items    **이미 우선순위대로 정렬된** 일정들. 먼저 온 것이 위 줄을 가져간다.
 * @param span     일정 하나의 시작·끝 날짜 글자('YYYY-MM-DD'). 끝이 시작보다 앞이면 하루짜리로 본다.
 * @param maxLanes 그릴 수 있는 줄 수. 넘치면 `overflow` 로 센다.
 */
export function layoutWeek<T>(
  weekDays: (string | null)[],
  items: T[],
  span: (item: T) => { start: string; end: string },
  maxLanes: number,
): WeekLayout<T> {
  const overflow = new Array<number>(7).fill(0);
  const firstCol = weekDays.findIndex((d) => d != null);
  // ⚠`findLastIndex` 는 이 프로젝트의 빌드 대상보다 새 문법이라 쓰지 않는다
  let lastCol = -1;
  for (let i = 6; i >= 0; i--) {
    if (weekDays[i] != null) { lastCol = i; break; }
  }
  if (firstCol < 0 || lastCol < 0) return { bars: [], overflow, laneCount: 0 };

  const weekStart = weekDays[firstCol]!;
  const weekEnd = weekDays[lastCol]!;

  /** 줄마다 「이 칸이 찼나」 7개 */
  const lanes: boolean[][] = [];
  const bars: CalendarBar<T>[] = [];

  for (const item of items) {
    const raw = span(item);
    const start = raw.start;
    const end = raw.end >= raw.start ? raw.end : raw.start;
    // 이 주와 아예 안 겹치면 볼 일이 없다
    if (end < weekStart || start > weekEnd) continue;

    // 주 바깥으로 삐져나간 쪽은 주의 끝에 붙인다 — 그쪽은 옆 주로 이어진다는 뜻이다
    const startsHere = start >= weekStart;
    const endsHere = end <= weekEnd;
    const from = startsHere ? weekDays.indexOf(start) : firstCol;
    const to = endsHere ? weekDays.indexOf(end) : lastCol;
    // 날짜 글자가 이 주의 칸과 안 맞으면(있을 수 없지만) 조용히 건너뛴다 — 잘못 그리는 것보다 낫다
    if (from < 0 || to < 0 || to < from) continue;

    let lane = 0;
    for (; lane < maxLanes; lane++) {
      let row = lanes[lane];
      if (!row) {
        row = new Array<boolean>(7).fill(false);
        lanes[lane] = row;
      }
      let free = true;
      for (let c = from; c <= to; c++) {
        if (row[c]) { free = false; break; }
      }
      if (free) {
        for (let c = from; c <= to; c++) row[c] = true;
        break;
      }
    }

    if (lane >= maxLanes) {
      // 자리를 못 얻었다 — 지나가는 칸마다 「외 N건」으로 센다
      for (let c = from; c <= to; c++) overflow[c] += 1;
      continue;
    }

    bars.push({ item, lane, from, to, startsHere, endsHere });
  }

  return { bars, overflow, laneCount: lanes.length };
}

/**
 * 달력 한 장 (v2.277.0 · 사용자 「캘린더에 전월·다음달의 날짜가 같은 줄에 있는 날짜들 표기 — 9월30일 다음에 10월1일, 2일 등」)
 *   그 달 1일이 든 주의 **일요일**부터 말일이 든 주의 **토요일**까지 진짜 날짜로 채운다(앞뒤 달 칸 포함 · 4~6주).
 *   전에는 앞뒤 달 칸이 빈칸(null)이었다. `from`·`to` = 일정을 불러올 기간(최대 42일 — 서버가 31일씩 나눠 웍스에 묻는다).
 */
export function monthGrid(y: number, m: number): { keys: string[]; from: string; to: string; inMonth: (key: string) => boolean } {
  const startWeekday = new Date(Date.UTC(y, m, 1)).getUTCDay();
  const lastDay = new Date(Date.UTC(y, m + 1, 0)).getUTCDate();
  const total = Math.ceil((startWeekday + lastDay) / 7) * 7;
  const keys = Array.from({ length: total }, (_, i) => new Date(Date.UTC(y, m, 1 - startWeekday + i)).toISOString().slice(0, 10));
  const prefix = `${y}-${String(m + 1).padStart(2, '0')}-`;
  return { keys, from: keys[0]!, to: keys[keys.length - 1]!, inMonth: (k) => k.startsWith(prefix) };
}

/** 42칸을 7칸씩 한 주로 자른다 */
export function toWeeks(cells: (string | null)[]): (string | null)[][] {
  const weeks: (string | null)[][] = [];
  for (let i = 0; i < cells.length; i += 7) weeks.push(cells.slice(i, i + 7));
  return weeks;
}
